import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ProgressRing } from "@/components/ProgressRing";
import { GARDENS } from "@/lib/gardens";

const STATE_COPY = {
    flourishing: { label: "Flourishing", note: "This ground is alive. Keep tending what is already bearing fruit." },
    growing: { label: "Growing", note: "Steady, faithful growth is happening here. Keep watering gently." },
    cultivating: { label: "Cultivating", note: "Fertile soil, ready for your care." },
};

export default function Gardens() {
    const [result, setResult] = useState(null);
    const location = useLocation();

    useEffect(() => { api.get("/assessment/latest").then((r) => setResult(r.data)); }, []);

    useEffect(() => {
        if (result && location.hash) {
            const el = document.getElementById(location.hash.replace("#", ""));
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [result, location.hash]);

    if (!result) return <div className="text-muted-foreground py-16">Walking your garden…</div>;

    return (
        <div className="space-y-32">
            <header className="max-w-3xl">
                <div className="eyebrow-gold mb-4">Your garden · walk-through</div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[1.02]">Eight areas. One garden.</h1>
                <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
                    Each area is an interwoven facet of your one life — some flourishing,
                    some quietly cultivating, all held together by the same soil.
                </p>
            </header>

            {result.categories.map((c, i) => {
                const meta = GARDENS.find((g) => g.key === c.key);
                if (!meta) return null;
                const stateInfo = STATE_COPY[c.state] || STATE_COPY.growing;
                return (
                    <motion.section
                        key={c.key}
                        id={c.key}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        data-testid={`garden-section-${c.key}`}
                    >
                        <div className="grid md:grid-cols-12 gap-10 items-center">
                            <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                                <div className="relative aspect-[16/11] rounded-lg overflow-hidden hairline">
                                    <img src={meta.image} alt={meta.label} className="absolute inset-0 w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                                    <div className="absolute inset-0 p-8 flex flex-col justify-end text-white">
                                        <div className="text-[10px] uppercase tracking-[0.35em] opacity-80">Area {String(i + 1).padStart(2, "0")}</div>
                                        <div className="font-serif text-4xl md:text-5xl mt-2">{meta.label}</div>
                                        <div className="font-serif text-lg md:text-xl mt-2 text-white/85 italic">{meta.virtue}</div>
                                    </div>
                                </div>
                            </div>
                            <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-1 md:pr-8" : "md:pl-8"}`}>
                                <div className="flex items-center gap-6">
                                    <ProgressRing value={c.score} size={120} stroke={2} label={stateInfo.label} />
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.35em] text-[hsl(var(--eden-gold))]">{c.label}</div>
                                        <div className="font-serif text-3xl mt-2 leading-tight">{meta.virtue}</div>
                                    </div>
                                </div>
                                <p className="mt-8 text-muted-foreground leading-relaxed">{meta.subtitle}</p>
                                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{stateInfo.note}</p>
                            </div>
                        </div>
                    </motion.section>
                );
            })}
        </div>
    );
}
